/**
 * A conversation. Direct chats and groups share this shape so the chat list
 * and the chat room each have one code path instead of two.
 *
 * @typedef {object} ChatParticipant
 * @property {string} userId
 * @property {'admin'|'member'} role
 * @property {Date}   joinedAt
 *
 * @typedef {object} LastMessagePreview
 * @property {string} id
 * @property {string} senderId
 * @property {string} type
 * @property {string} preview     Already-truncated text for the list row
 * @property {Date}   createdAt
 *
 * @typedef {object} Chat
 * @property {string}      id
 * @property {'direct'|'group'} type
 * @property {string|null} name        Groups only; direct chats show the other user
 * @property {string|null} description Groups only
 * @property {string|null} avatarUrl
 * @property {string[]}    participantIds   Flat array - Firestore array-contains needs it
 * @property {ChatParticipant[]} participants
 * @property {LastMessagePreview|null} lastMessage
 * @property {Date|null}   lastMessageAt
 * @property {number}      unreadCount
 * @property {boolean}     isMuted
 * @property {boolean}     isPinned
 * @property {boolean}     isArchived
 * @property {string|null} communityId     The community this group is under
 * @property {boolean}     isAnnouncement  Its community's announcement group
 * @property {string}      createdBy
 * @property {Date}        createdAt
 */

import { toDate } from '../utils/time.js'

/**
 * @param {string} id
 * @param {object} raw
 * @param {string} [currentUserId] Used to resolve per-user flags
 * @returns {Chat}
 */
export function toChat(id, raw = {}, currentUserId) {
  // A participant is a membership AND the person it belongs to. Group info
  // needs a name beside every row, and a screen that has to fetch each user
  // separately to render a list is a screen that renders slowly and wrongly.
  // Providers that know the names fill these in; those that do not leave
  // them undefined.
  const participants = (raw.participants ?? []).map(p => ({
    userId:      p.userId ?? p.user_id,
    role:        p.role ?? 'member',
    joinedAt:    toDate(p.joinedAt ?? p.joined_at) ?? new Date(),
    displayName: p.displayName ?? p.display_name,
    avatarUrl:   p.avatarUrl ?? p.avatar_url,
    about:       p.about,
    isOnline:    p.isOnline ?? p.is_online
  }))

  const last = raw.lastMessage ?? raw.last_message ?? null

  return {
    id,
    type:        raw.type ?? 'direct',
    name:        raw.name ?? null,

    /**
     * What to show as the chat's heading.
     *
     * Groups have a name; direct chats do not — the heading is the other
     * person. Each provider resolves that and passes it as peerName, since
     * only the provider can see the user records. Without this every
     * one-to-one conversation in the list read "Chat".
     */
    title: raw.name ?? raw.peerName ?? raw.peer_name ?? null,
    peerAvatarUrl: raw.peerAvatarUrl ?? raw.peer_avatar_url ?? null,
    description: raw.description ?? null,
    avatarUrl:   raw.avatarUrl ?? raw.avatar_url ?? null,

    participantIds: raw.participantIds ?? raw.participant_ids ?? participants.map(p => p.userId),
    participants,

    lastMessage: last
      ? {
          id:        last.id,
          senderId:  last.senderId ?? last.sender_id,
          type:      last.type ?? 'text',
          preview:   last.preview ?? last.body ?? '',
          // Set once the newest message has been deleted for everyone, so a
          // row can draw the tombstone glyph and choose its wording.
          deleted:   Boolean(last.deleted ?? last.deletedAt ?? last.deleted_at),
          createdAt: toDate(last.createdAt ?? last.created_at) ?? new Date()
        }
      : null,
    lastMessageAt: toDate(raw.lastMessageAt ?? raw.last_message_at),

    // Per-user flags live in a map keyed by user id, so one chat document
    // serves every participant without a document-per-user fan-out.
    unreadCount: readUserFlag(raw, currentUserId, 'unreadCount', 0, 'unreadCount', 'unread_count'),
    isMuted:     Boolean(readUserFlag(raw, currentUserId, 'muted', false, 'isMuted', 'is_muted')),
    isPinned:    Boolean(readUserFlag(raw, currentUserId, 'pinned', false, 'isPinned', 'is_pinned')),
    isArchived:  Boolean(readUserFlag(raw, currentUserId, 'archived', false, 'isArchived', 'is_archived')),

    /*
     * Communities.
     *
     * A community's group is an ordinary group chat that gained a parent,
     * so these ride on Chat rather than on a type of their own. Null for
     * every chat that belongs to no community, which is most of them.
     *
     * `?? null` rather than a falsy default: the chat list and the group
     * list both ask "is this in a community", and `undefined` there would
     * read the same as "no" while meaning "the provider did not say".
     */
    communityId:    raw.communityId ?? raw.community_id ?? null,
    isAnnouncement: Boolean(raw.isAnnouncement ?? raw.is_announcement),

    createdBy: raw.createdBy ?? raw.created_by ?? '',
    createdAt: toDate(raw.createdAt ?? raw.created_at) ?? new Date()
  }
}

/**
 * Read a per-user flag from whichever shape the provider speaks.
 *
 * Firebase keeps these in a map on the chat document, keyed by user id, so
 * one document serves every participant. The REST server cannot: its row
 * comes from a join on chat_participants and is already scoped to the
 * caller, so it sends the flag flat — `unreadCount`, `isPinned`.
 *
 * Only the nested shape was read here, which meant that on the REST
 * backend the badge was always 0, nothing was ever pinned or muted, and
 * the Archived row never appeared — silently, because a missing flag and
 * a false flag look the same (owner's screenshot, 2026-11-04).
 *
 * The nested value still wins where it exists, so Firebase is unchanged.
 */
function readUserFlag(raw, userId, nestedKey, fallback, ...flatKeys) {
  const perUser = raw.userState ?? raw.user_state ?? {}
  const nested = userId ? perUser[userId]?.[nestedKey] : undefined
  if (nested !== undefined) return nested

  for (const key of flatKeys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key]
  }
  return fallback
}

/** The other person in a direct chat. Returns null for groups. */
export function otherParticipantId(chat, currentUserId) {
  if (chat.type !== 'direct') return null
  return chat.participantIds.find(id => id !== currentUserId) ?? null
}

/** Is this user an admin of this group? */
export function isAdmin(chat, userId) {
  return chat.participants.some(p => p.userId === userId && p.role === 'admin')
}
